import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, ArrowLeft, Send, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  NUOVA: "bg-sky-100 text-sky-700", IN_LAVORAZIONE: "bg-amber-100 text-amber-700",
  IN_ATTESA_CLIENTE: "bg-purple-100 text-purple-700", RISOLTA: "bg-emerald-100 text-emerald-700",
  CHIUSA: "bg-slate-200 text-slate-600",
};

export default function Assistenza() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "generale", priority: "normale", message: "" });
  const [reply, setReply] = useState("");

  const load = () => http.get("/support/tickets").then((r) => setTickets(r.data.tickets || [])).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const openTicket = (id) => http.get(`/support/tickets/${id}`).then((r) => setActive(r.data)).catch((e) => toast.error(apiErr(e)));

  const create = async () => {
    if (!form.subject.trim() || !form.message.trim()) return toast.error("Compila oggetto e messaggio");
    try { await http.post("/support/tickets", form); toast.success("Richiesta creata"); setCreating(false); setForm({ subject: "", category: "generale", priority: "normale", message: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await http.post(`/support/tickets/${active.id}/messages`, { message: reply }); setReply(""); openTicket(active.id); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (active) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="ticket-detail">
        <button onClick={() => { setActive(null); load(); }} className="flex items-center gap-1 text-sm text-slate-500 mb-4"><ArrowLeft className="w-4 h-4" /> {t("support.title")}</button>
        <div className="flex items-center justify-between gap-2 mb-3">
          <h1 className="font-heading text-xl font-extrabold text-[#0F172A]">{active.subject}</h1>
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[active.status] || ""}`}>{active.status}</span>
        </div>
        <div className="space-y-2 mb-4">
          {active.messages.filter((m) => m.author_role !== "ASSISTANT_CONTEXT").map((m) => (
            <div key={m.id} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.author_role === "OWNER" ? "bg-white border border-slate-200" : "ml-auto bg-sky-600 text-white"}`}>
              <div className="text-[10px] opacity-70 mb-0.5">{m.author_name}</div>{m.text}
            </div>
          ))}
        </div>
        {!["CHIUSA", "RISOLTA"].includes(active.status) && (
          <div className="flex items-center gap-2">
            <Input data-testid="ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("support.reply")} onKeyDown={(e) => e.key === "Enter" && sendReply()} />
            <Button data-testid="ticket-reply-send" onClick={sendReply}><Send className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="assistenza-page">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-6 h-6 text-sky-600" />
          <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("support.title")}</h1>
        </div>
        <Button data-testid="new-ticket-btn" onClick={() => setCreating(true)} className="bg-[#0F172A] gap-1.5"><Plus className="w-4 h-4" /> {t("support.new")}</Button>
      </div>

      {creating && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 space-y-3" data-testid="new-ticket-form">
          <Input data-testid="ticket-subject" placeholder={t("support.subject")} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="ticket-category" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              <option value="generale">{t("support.category")}: generale</option>
              <option value="tecnico">tecnico</option>
              <option value="fatturazione">fatturazione</option>
              <option value="whatsapp">whatsapp</option>
            </select>
            <select data-testid="ticket-priority" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="normale">{t("support.normal")}</option>
              <option value="urgente">{t("support.urgent")}</option>
            </select>
          </div>
          <textarea data-testid="ticket-message" rows={4} placeholder={t("support.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <Button data-testid="ticket-create-submit" onClick={create} className="bg-sky-600">{t("support.create")}</Button>
            <Button variant="outline" onClick={() => setCreating(false)}>✕</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {tickets.length === 0 && <div className="text-slate-400 text-sm text-center py-10">{t("support.empty")}</div>}
        {tickets.map((tk) => (
          <button key={tk.id} data-testid={`ticket-row-${tk.id}`} onClick={() => openTicket(tk.id)}
            className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 hover:border-sky-300 text-left">
            <div className="min-w-0">
              <div className="font-semibold text-[#0F172A] truncate flex items-center gap-2">
                {tk.subject}
                {tk.unread_tenant && <span className="w-2 h-2 rounded-full bg-sky-500" />}
              </div>
              <div className="text-xs text-slate-500">{tk.category} · {tk.priority}</div>
            </div>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[tk.status] || ""}`}>{tk.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
